/** biome-ignore-all lint/performance/noImgElement: Dynamic GCS-hosted assets */
/** biome-ignore-all lint/a11y/useMediaCaption: Raw ad creatives, no captions exist */
"use client";

import { Play, VideoIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { Button } from "@/components/ui/design-system/button";
import { Shelf, ShelfItem } from "@/components/ui/design-system/shelf";
import { CONTROL_PRESS } from "@/components/ui/design-system/surface-styles";
import { Caption, Hint, Label } from "@/components/ui/design-system/text";
import { VideoFeed } from "@/components/ui/design-system/video-feed";
import type { RouterOutput } from "@/lib/trpc-client";
import { cn } from "@/lib/utils";
import { PORTAL_OVERLAY } from "./one/chrome";
import { FIGURE } from "./one/ui";
import { useCreatorVideoFeedLabels } from "./useCreatorVideoFeedLabels";

type TopAd = RouterOutput["creatorPortal"]["getPortalTopAds"]["topAds"][number];

function formatUsd(value: number): string {
  return value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

/**
 * The payout as the tile and the feed print it. The server rounds anything
 * under $500 to 0 rather than floor it at a figure the ad would not earn.
 */
function payoutText(ad: TopAd, small: string): string {
  return ad.estPayoutUsd > 0 ? formatUsd(ad.estPayoutUsd) : small;
}

function TopAdCard({
  ad,
  rank,
  onExpand,
}: {
  ad: TopAd;
  rank: number;
  onExpand: () => void;
}) {
  const t = useTranslations("home");
  const { videoUrl, thumbnailUrl } = ad;
  const payoutLabel = payoutText(ad, t("topAds.estimatedPayoutSmall"));
  const media = videoUrl ? (
    <video
      src={videoUrl}
      poster={thumbnailUrl ?? undefined}
      className="absolute inset-0 h-full w-full object-cover"
      muted
      playsInline
      loop
      preload="metadata"
      onMouseEnter={(e) => void e.currentTarget.play().catch(() => {})}
      onMouseLeave={(e) => {
        e.currentTarget.pause();
        e.currentTarget.currentTime = 0;
      }}
    />
  ) : thumbnailUrl ? (
    <img
      src={thumbnailUrl}
      alt={ad.adName}
      className="absolute inset-0 h-full w-full object-cover"
    />
  ) : (
    <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
      <VideoIcon className="h-10 w-10" strokeWidth={1.25} />
    </div>
  );

  return (
    /*
      NO CARD AROUND THIS. The thumbnail carries the radius on its own and the
      payout sits under it, on the page — which is the shape a shelf of media
      wants. Wrapped in a pane the tile became a card containing a picture,
      with two radii an inch apart and a band of card colour under every
      video; the row read as a list of documents rather than as a row of work.
    */
    // Wider on a phone, where one tile is centred and has the screen to
    // itself, and back to the row width once several sit side by side.
    <div className="group flex w-[260px] flex-col gap-nf-ds-2 lg:w-[220px]">
      <div
        className={cn(
          // 2:3 rather than the 4:5 the rest of the app crops ads to.
          //
          // These are vertical videos, so every point of height is more of the
          // real frame and less of a crop — and this row is the one place the
          // work is being shown off rather than listed. It stops well short of
          // the full 9:16, which at this width would be a 391-point tile: a
          // single ad taller than the card above it, with the hook — the part
          // worth studying — pushed into the top third. 2:3 is 330 points: a
          // clear step up from 275 without the tile becoming the page.
          "relative aspect-[2/3] overflow-hidden bg-nf-ds-surface-card",
          "rounded-nf-ds-card ring-1 ring-nf-ds-surface-card-ring ring-inset",
        )}
      >
        {videoUrl ? (
          <button
            type="button"
            aria-label={t("topAds.watch", { rank })}
            className="absolute inset-0 cursor-pointer focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-nf-ds-purple-product/60 focus-visible:ring-inset"
            onClick={(e) => {
              // The muted hover-preview doesn't get a mouseleave when the
              // feed opens on top of it — stop it explicitly so it isn't
              // looping behind the feed.
              const preview = e.currentTarget.querySelector("video");
              if (preview) {
                preview.pause();
                preview.currentTime = 0;
              }
              onExpand();
            }}
          >
            {media}
          </button>
        ) : (
          media
        )}

        <div className="absolute top-nf-ds-2 left-nf-ds-2 z-10 pointer-events-none">
          <span className="inline-flex items-center h-[22px] px-2 rounded-nf-ds-sm bg-black/65 backdrop-blur-sm text-white font-nf-ds-mono text-[10px] uppercase tracking-[0.08em]">
            #{rank}
          </span>
        </div>

        {videoUrl && (
          <div
            aria-hidden
            className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-90 group-hover:opacity-0 transition-opacity"
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-black/55 backdrop-blur-sm">
              <Play className="h-5 w-5 fill-white text-white" strokeWidth={0} />
            </span>
          </div>
        )}
      </div>

      {/* Outside the media and outside any container — a caption under a
          picture, inset by a hair so it sits over the thumbnail's edge rather
          than flush with it. */}
      {/*
        Both halves sans and both the same colour, with the figure carrying the
        weight.

        This was a mono uppercase `Rail` against a brand-purple `Strong` on one
        line: two typefaces and two colours for one sentence, with the label at
        the far left and its number at the far right. Stacked and centred they
        are a caption and the thing it names — the name first, small, then the
        figure at the `Figure` role, which is what the scale calls the big
        number. One colour, so the pair reads as one statement and the size
        does the hierarchy.

        Spelled out, too. "Est." saves four characters in a caption that has
        the width for the word, and an abbreviation is a small tax on every
        reader to save space nobody needed.
      */}
      <div className="flex flex-col items-center gap-nf-ds-tight px-nf-ds-1 text-center">
        <Label>{t("topAds.estimatedPayout")}</Label>
        {/* The same figure treatment the balance on Home uses, from the same
            constant — a payout under a tile and a balance on a card are the
            same kind of number and should not be two sizes. */}
        <span className={FIGURE}>{payoutLabel}</span>
      </div>
    </div>
  );
}

function TopAdCardSkeleton() {
  return (
    <div className="flex w-[260px] flex-col gap-nf-ds-2 lg:w-[220px]">
      <div className="aspect-[2/3] animate-pulse rounded-nf-ds-card bg-nf-ds-surface-card" />
      <div className="mx-nf-ds-1 h-3 animate-pulse rounded-nf-ds-sm bg-nf-ds-surface-card" />
    </div>
  );
}

/**
 * What a top ad says over itself in the feed: its place on the board, then
 * the payout with its name over it, the same pair the tile prints under it.
 * The money is the figure because it is why a creator is watching.
 */
function TopAdFeedCaption({ ad, rank }: { ad: TopAd; rank: number }) {
  const t = useTranslations("home");
  return (
    <div className="flex flex-col items-start gap-nf-ds-tight">
      <Caption className="rounded-nf-ds-pill bg-nf-ds-white/15 px-nf-ds-2 py-nf-ds-1">
        #{rank}
      </Caption>
      <Label>{t("topAds.estimatedPayout")}</Label>
      <span className={FIGURE}>
        {payoutText(ad, t("topAds.estimatedPayoutSmall"))}
      </span>
    </div>
  );
}

/**
 * The row's own control, opposite its title. A label rather than a filled
 * button: "Submit a video" is the page's one filled control. The target is
 * the phone's 48 points and overhangs the title's line rather than pushing
 * the subtitle down by the difference.
 */
function WatchAll({ onClick }: { onClick: () => void }) {
  const t = useTranslations("home");
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        CONTROL_PRESS,
        "-my-nf-ds-3 -mr-nf-ds-3 inline-flex h-nf-ds-12 shrink-0 items-center gap-nf-ds-2 rounded-nf-ds-button px-nf-ds-3",
        "font-nf-ds-sans text-nf-ds-label text-nf-ds-brand outline-hidden focus-visible:ring-2 focus-visible:ring-nf-ds-brand",
        "[@media(hover:hover)_and_(pointer:fine)]:hover:text-nf-ds-brand-hover",
      )}
    >
      <Play aria-hidden className="size-nf-ds-4 fill-current" strokeWidth={0} />
      {t("topAds.watchAll")}
    </button>
  );
}

export function TopAdsRow({
  ads,
  isLoading,
  isError,
  onRetry,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
}: {
  /** Every page loaded so far, ranked in order. */
  ads: TopAd[];
  isLoading: boolean;
  isError?: boolean;
  /** Refetch: the error state's retry, and the feed's for an expired link. */
  onRetry?: () => void;
  hasMore?: boolean;
  loadingMore?: boolean;
  /** The next page of the board, asked for by the feed as it nears the end. */
  onLoadMore?: () => void;
}) {
  const t = useTranslations("home");
  /** Where the feed is open, as an index into `playable`. */
  const [feedIndex, setFeedIndex] = useState<number | null>(null);
  const feedLabels = useCreatorVideoFeedLabels();
  // The rank is the ad's place on the whole board, so it is taken before the
  // ads without a file drop out of the feed.
  const playable = ads.flatMap((ad, index) =>
    ad.videoUrl ? [{ ad, rank: index + 1 }] : [],
  );
  const feedItems = playable.map(({ ad, rank }) => ({
    id: ad.id,
    src: ad.videoUrl,
    poster: ad.thumbnailUrl,
    label: t("topAds.lightboxTitle", { rank }),
    ad,
    rank,
  }));
  const openAd = (id: string) => {
    const index = playable.findIndex((entry) => entry.ad.id === id);
    if (index >= 0) setFeedIndex(index);
  };

  return (
    <>
      <Shelf
        title={t("topAds.title")}
        subtitle={t("topAds.subtitle")}
        action={
          !isLoading && playable.length > 0 ? (
            <WatchAll onClick={() => setFeedIndex(0)} />
          ) : undefined
        }
        // Edge to edge under a thumb, inside the column at a desk.
        //
        // On a phone a row that stops short of the screen reads as a list that
        // finished, and the gesture wants the whole width. At desk width the
        // opposite is true: the shelf is one block among several, and a row
        // running past the cards above it is the only thing on the page
        // ignoring the column everything else is aligned to.
        //
        /*
          Full width at every size, like every other section, with the tiles at
          the size they were.

          Those two together settle the count rather than the other way round:
          220-point tiles in a 1104-point column means the row shows three in
          the middle and a good part of their neighbours either side. Forcing
          it to exactly three needs tiles half again as wide, which was tried
          twice and is the wrong thing to spend the size on.

          The negative margin at rail width is half a gap, and it cancels the
          half-gap padding the first slide carries: without it the first tile
          opens 12 points right of the heading above it.

          NOT WITH A MARGIN, AT RAIL WIDTH. `trackClassName` lands on the
          scrolling viewport, so a negative margin there does not nudge the
          tiles, it widens the window they scroll behind: `-mx` put the row
          12 points past the column at both ends, and `-ml` alone moved the
          same leak to the left, where a mid-scroll tile shows in the strip
          outside the column. Both were the right idea applied to the wrong
          box.

          The half-gap belongs to the slides, so that is where it is taken
          back: the first loses its leading padding and the last its
          trailing one, below. The viewport then measures exactly the
          column, which is the only way the row cannot leak at either end
          however far it is scrolled, and the first tile still opens on the
          heading's own left edge.

          A phone keeps the margin, because there it is doing the opposite
          job on purpose: the row runs to both screen edges and the padding
          puts the first tile back on the gutter.
        */
        trackClassName="-mx-nf-ds-6 px-nf-ds-6 lg:mx-0 lg:px-0"
      >
        {isLoading
          ? Array.from({ length: 5 }, (_, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: static placeholder list
              <ShelfItem key={i}>
                <TopAdCardSkeleton />
              </ShelfItem>
            ))
          : ads.map((ad, index) => (
              <ShelfItem
                key={ad.id}
                // The ends give back their half-gap so the row starts and
                // finishes on the column, with the viewport left alone. See
                // the note on `trackClassName`.
                className={cn(
                  index === 0 && "lg:pl-0",
                  index === ads.length - 1 && "lg:pr-0",
                )}
              >
                <TopAdCard
                  ad={ad}
                  rank={index + 1}
                  onExpand={() => openAd(ad.id)}
                />
              </ShelfItem>
            ))}
      </Shelf>

      {isError ? (
        <div className="flex items-center gap-nf-ds-3">
          <Hint muted>{t("topAds.loadFailed")}</Hint>
          {onRetry ? (
            <Button variant="outline" size="sm" onClick={onRetry}>
              {t("topAds.retry")}
            </Button>
          ) : null}
        </div>
      ) : null}

      {!isLoading && !isError && ads.length === 0 ? (
        <Hint muted>{t("topAds.empty")}</Hint>
      ) : null}

      <VideoFeed
        labels={feedLabels}
        open={feedIndex !== null}
        startIndex={feedIndex ?? 0}
        onClose={() => setFeedIndex(null)}
        title={t("topAds.title")}
        items={feedItems}
        renderCaption={(item) => (
          <TopAdFeedCaption ad={item.ad} rank={item.rank} />
        )}
        hasMore={hasMore}
        loadingMore={loadingMore}
        onEndReached={onLoadMore}
        onRetry={onRetry}
        endNote
        className={PORTAL_OVERLAY}
      />
    </>
  );
}
